// Operations tab — within-Workspace cockpit. Per the 2026-04-29
// Captain canon, "Operating" happens INSIDE a single Workspace
// (parallel agents on worktrees still count as Operating); macro
// cross-Workspace concerns live on the top-level /orchestrator
// route, not in here. Anything that lists OTHER Workspaces in this
// tab is a canon error and should be moved out.

import {
  Activity,
  GitCommit,
  Network,
  PauseCircle,
  ShieldCheck,
  Timer,
  Zap,
} from "lucide-react";
import type { ReactNode } from "react";
import { TabScrollbox } from "../../chrome/TabScrollbox";
import type { Entitlements } from "../../api/client";
import type { Agent, AgentStatus, Message, Workspace } from "../types";

interface InflightAgent {
  agentId: number;
  status: string;
}

interface Props {
  workspace: Workspace | null;
  agents: Agent[];
  presenceByAgentId: Map<number, AgentStatus>;
  activeInflight: InflightAgent[];
  activeRoutingCount: number;
  pendingCommitCount: number;
  pendingTurnCount: number;
  recentMessages: Message[];
  workspacePaused: boolean;
  entitlements: Entitlements | null;
}

const WATCHER_HINTS = [
  "watch",
  "drift",
  "halfassed",
  "canon",
  "review",
  "research",
  "sentry",
  "input",
  "tweak",
  "shore",
  "compass",
  "reef",
];

export function OperatorTab({
  workspace,
  agents,
  presenceByAgentId,
  activeInflight,
  activeRoutingCount,
  pendingCommitCount,
  pendingTurnCount,
  recentMessages,
  workspacePaused,
  entitlements,
}: Props) {
  const watcherAgents = agents.filter(isWatcherAgent);
  const workingAgents = agents.filter((a) => !isWatcherAgent(a));
  const cap = entitlements?.daily_compute_minutes_cap ?? 0;
  const used = entitlements?.daily_compute_minutes_used ?? 0;
  const remaining = Math.max(0, cap - used);
  const sandboxState = workspacePaused
    ? "paused"
    : workspace?.daytona_state || "cold";
  const liveLaneCount = activeInflight.length + activeRoutingCount;

  return (
    <TabScrollbox testId="operator-tab">
      <div className="flex w-full flex-col gap-4">
        <header className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr,26rem]">
          <div className="border-b border-edge pb-3">
            <p className="font-code text-[0.625rem] text-faint">Operations</p>
            <div className="mt-1 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-primary">
                  {workspace?.name ?? "Workspace"}
                </h2>
                <p className="mt-1 max-w-3xl text-xs text-muted">
                  Operating: every running unit, watcher, and queued judgment
                  inside this Workspace — including parallel agents on
                  worktrees. Macro view across Workspaces lives outside, on
                  the Orchestrator route.
                </p>
              </div>
              <StatePill state={sandboxState} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <MetricCard
              icon={<Activity size={14} />}
              label="Live lanes"
              value={String(liveLaneCount)}
              detail={`${activeInflight.length} turns / ${activeRoutingCount} routes`}
            />
            <MetricCard
              icon={<GitCommit size={14} />}
              label="Evidence queue"
              value={String(pendingCommitCount)}
              detail="commit approvals"
            />
            <MetricCard
              icon={<Timer size={14} />}
              label="Compute"
              value={`${used}m`}
              detail={cap > 0 ? `${remaining}m left today` : "cap unresolved"}
            />
            <MetricCard
              icon={<Zap size={14} />}
              label="Shadow"
              value={entitlements?.shadow_teams_enabled ? "on" : "gated"}
              detail={entitlements?.pooled_models_available ? "pooled models" : "BYOK/subs"}
            />
          </div>
        </header>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.1fr),minmax(20rem,0.9fr)]">
          <Panel
            title="Live Lanes"
            icon={<Network size={14} />}
            testId="operator-live-lanes"
          >
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              <LaneSummary
                label="Pending turns"
                value={pendingTurnCount}
                tone={pendingTurnCount > 0 ? "accent" : "muted"}
              />
              <LaneSummary
                label="Routing locks"
                value={activeRoutingCount}
                tone={activeRoutingCount > 0 ? "accent" : "muted"}
              />
            </div>
            {activeInflight.length === 0 ? (
              <p className="mt-3 text-xs text-faint">
                No active agent turn is currently waiting on the roster.
              </p>
            ) : (
              <ul className="mt-3 flex flex-col gap-2">
                {activeInflight.map((lane) => {
                  const agent = agents.find((a) => a.agent_id === lane.agentId);
                  return (
                    <li
                      key={lane.agentId}
                      className="rounded-md border border-edge bg-surface-raised px-3 py-2"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-xs font-medium text-primary">
                            {agent?.display_name ?? `Agent ${lane.agentId}`}
                          </p>
                          <p className="truncate font-code text-[0.625rem] text-faint">
                            {agent?.current_model ?? "model pending"}
                          </p>
                        </div>
                        <StatePill state={lane.status} compact />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>

          <Panel
            title="Watcher Layer"
            icon={<ShieldCheck size={14} />}
            testId="operator-watchers"
          >
            {watcherAgents.length === 0 ? (
              <p className="text-xs text-warning">
                No watcher-class seats are present in this workspace roster.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {watcherAgents.map((agent) => (
                  <AgentRow
                    key={agent.agent_id}
                    agent={agent}
                    status={presenceByAgentId.get(agent.agent_id) ?? "idle"}
                  />
                ))}
              </ul>
            )}
            {workingAgents.length > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer text-xs text-muted hover:text-primary">
                  Working seats
                </summary>
                <ul className="mt-2 flex flex-col gap-2">
                  {workingAgents.map((agent) => (
                    <AgentRow
                      key={agent.agent_id}
                      agent={agent}
                      status={presenceByAgentId.get(agent.agent_id) ?? "idle"}
                    />
                  ))}
                </ul>
              </details>
            )}
          </Panel>
        </section>

        <section className="grid grid-cols-1 gap-4">
          {/* Cross-Workspace ("Workspace Stack") panel removed
              2026-04-29 per Captain canon: Operating is within a
              single Workspace (parallel agents on worktrees still
              count as Operating). Cross-Workspace listing is an
              Orchestrating concern and lives on the top-level
              /orchestrator route, not inside a Workspace. */}
          <Panel
            title="Judgment Queue"
            icon={workspacePaused ? <PauseCircle size={14} /> : <GitCommit size={14} />}
            testId="operator-judgment"
          >
            <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
              <LaneSummary
                label="Commit approvals"
                value={pendingCommitCount}
                tone={pendingCommitCount > 0 ? "warning" : "muted"}
              />
              <LaneSummary
                label="Team messages"
                value={recentMessages.length}
                tone={recentMessages.length > 0 ? "accent" : "muted"}
              />
              <LaneSummary
                label="Workspace"
                value={workspacePaused ? "paused" : "open"}
                tone={workspacePaused ? "warning" : "muted"}
              />
            </div>
            <ul className="mt-3 flex flex-col gap-2">
              {recentMessages.length === 0 ? (
                <li className="text-xs text-faint">No recent team messages.</li>
              ) : (
                recentMessages.map((message) => (
                  <li
                    key={message.message_id}
                    className="rounded-md border border-edge bg-surface-raised px-3 py-2"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="line-clamp-2 min-w-0 text-xs text-primary">
                        {message.body}
                      </p>
                      <span className="shrink-0 font-code text-[0.625rem] text-faint">
                        {message.author_kind}
                      </span>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </Panel>
        </section>
      </div>
    </TabScrollbox>
  );
}

function isWatcherAgent(agent: Agent) {
  const haystack = `${agent.seat_key} ${agent.display_name} ${agent.role_title}`.toLowerCase();
  return WATCHER_HINTS.some((hint) => haystack.includes(hint));
}

function Panel({
  title,
  icon,
  children,
  testId,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
  testId: string;
}) {
  return (
    <section
      className="rounded-md border border-edge bg-surface p-3"
      data-testid={testId}
    >
      <header className="mb-3 flex items-center gap-2 border-b border-edge pb-2 text-xs font-semibold text-primary">
        <span className="text-accent">{icon}</span>
        <h3>{title}</h3>
      </header>
      {children}
    </section>
  );
}

function MetricCard({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="rounded-md border border-edge bg-surface px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-muted">{icon}</span>
        <span className="font-code text-[0.625rem] text-faint">{label}</span>
      </div>
      <p className="mt-2 text-lg font-semibold text-primary">{value}</p>
      <p className="mt-0.5 truncate text-[0.625rem] text-muted">{detail}</p>
    </div>
  );
}

function LaneSummary({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone: "accent" | "warning" | "muted";
}) {
  const color =
    tone === "accent"
      ? "text-accent"
      : tone === "warning"
        ? "text-warning"
        : "text-muted";
  return (
    <div className="rounded-md border border-edge bg-surface-raised px-3 py-2">
      <p className="font-code text-[0.625rem] text-faint">{label}</p>
      <p className={`mt-1 text-sm font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function AgentRow({
  agent,
  status,
}: {
  agent: Agent;
  status: AgentStatus;
}) {
  return (
    <li className="rounded-md border border-edge bg-surface-raised px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-primary">
            {agent.display_name}
          </p>
          <p className="truncate text-[0.625rem] text-muted">
            {agent.role_title}
          </p>
          <p className="truncate font-code text-[0.625rem] text-faint">
            {agent.current_model}
          </p>
        </div>
        <StatePill state={status} compact />
      </div>
    </li>
  );
}

function StatePill({ state, compact = false }: { state: string; compact?: boolean }) {
  const normalised = state.toLowerCase();
  const color =
    normalised.includes("error") || normalised.includes("failed")
      ? "border-danger text-danger"
      : normalised.includes("paused") || normalised.includes("blocked")
        ? "border-warning text-warning"
        : normalised.includes("running") ||
            normalised.includes("ready") ||
            normalised.includes("thinking") ||
            normalised.includes("replying")
          ? "border-accent text-accent"
          : "border-edge text-muted";
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-md border ${color} ${
        compact ? "px-1.5 py-0.5" : "px-2 py-1"
      } font-code text-[0.625rem]`}
    >
      {state || "unknown"}
    </span>
  );
}
